import { useCreateVetterUserMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const EMAIL_RE = /^\S+@\S+\.\S+$/;

/**
 * Adds a colleague to the supplier and emails them the usual invitation to
 * set a password. The backend only ever creates them under the caller's own
 * supplier; `vetterEntityId` is passed because the input requires it.
 */
export function InviteMemberModal({
	open,
	onOpenChange,
	vetterEntityId,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	vetterEntityId: string;
	onSuccess: () => void;
}) {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [createUser, { loading }] = useCreateVetterUserMutation();

	const reset = () => {
		setFirstName('');
		setLastName('');
		setEmail('');
		setError(null);
	};

	const canSubmit = !!firstName.trim() && !!lastName.trim() && EMAIL_RE.test(email.trim());

	const handleSubmit = async () => {
		if (!canSubmit || loading) return;
		setError(null);
		try {
			const { data } = await createUser({
				variables: {
					input: {
						vetterEntityId,
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						email: email.trim(),
						sendInvitation: true,
					},
				},
			});
			const res = data?.createVetterUser;
			if (!res?.success) {
				setError(res?.message || 'Could not send the invitation.');
				return;
			}
			toast.success(`Invitation sent to ${email.trim()}`);
			reset();
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not send the invitation.');
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) reset();
				onOpenChange(o);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Invite a colleague</DialogTitle>
					<DialogDescription>
						They get an email to set a password, then see every job for your company.
					</DialogDescription>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label>
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										autoFocus
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										disabled={loading}
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label>
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										disabled={loading}
									/>
								</div>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>
									Email <span className="text-destructive">*</span>
								</Label>
								<Input
									type="email"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										if (error) setError(null);
									}}
									placeholder="name@company.com"
									disabled={loading}
								/>
							</div>
							{error && <div className="text-sm text-status-danger">{error}</div>}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading ? 'Sending…' : 'Send invitation'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
